/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/shared/contributorFormValidation.ts.
 */

import type { FormField } from "../../../../../interfaces/formInterfaces";
import { resolveContributorFieldDisplayLabel } from "./contributorFieldDisplayLabels";

/** Type definition — Contributor Form Errors. */
export type ContributorFormErrors = Record<string, string>;

/** Exported function — get First Form Error. */
export function getFirstFormError(
  errors: ContributorFormErrors
): string | null {
  const messages = Object.values(errors);
  return messages.length > 0 ? messages[0] : null;
}

/** Exported function — field Label. */
export function fieldLabel(field: FormField): string {
  return resolveContributorFieldDisplayLabel(field);
}

/** Exported function — has Field Value. */
export function hasFieldValue(value: unknown): boolean {
  if (value instanceof File) return true;
  return String(value ?? "").trim() !== "";
}

/** True when a UNIT field has a stored CMS id, token (kWh/MJ), or other non-empty value. */
export function hasContributorUnitFieldValue(value: unknown): boolean {
  const s = String(value ?? "").trim();
  if (!s) return false;
  if (/^\d+$/.test(s)) return true;
  const lower = s.toLowerCase();
  if (lower === "kwh" || lower === "mj") return true;
  return true;
}

/** Exported function — build Section Field Key. */
export function buildSectionFieldKey(
  fieldName: string,
  sectionCode: string,
  rowIndex: number
): string {
  return `${fieldName}_${sectionCode}_row${rowIndex}`;
}

/** Exported function — clear Field Errors From Map. */
export function clearFieldErrorsFromMap(
  prev: ContributorFormErrors,
  ...names: string[]
): ContributorFormErrors {
  const hasMatch = names.some((name) => prev[name]);
  if (!hasMatch) return prev;
  const next = { ...prev };
  names.forEach((name) => {
    delete next[name];
  });
  return next;
}
